import { useEffect, useState } from 'preact/hooks'

import { formatMoney, globalState } from 'helpers/utils'

import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import useURL from 'hooks/useUrl'

import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import { createPortal } from 'preact/compat'
import clsx from 'clsx'

const actionKeys = {
  PLUS: 'plus',
  MINUS: 'minus',
  REMOVE: 'remove'
}

const ariaLabels = {
  [actionKeys.PLUS]: 'Increase quantity',
  [actionKeys.MINUS]: 'Decrease quantity',
  [actionKeys.REMOVE]: 'Remove item'
}

const onChangeActionValue = (key, quantity) => {
  return {
    [actionKeys.PLUS]: quantity + 1,
    [actionKeys.MINUS]: quantity - 1,
    [actionKeys.REMOVE]: 0
  }[key]
}

const OnChangeTrigger = ({ type, value, className, p = 'p-2' }) => {
  return (
    <div class={`relative my-auto h-full group flex-grow-0 cursor-pointer ${p}`}>
      <input
        type="checkbox"
        data-action={type}
        aria-label={ariaLabels[type]}
        name={ariaLabels[type]}
        class="absolute inset-0 h-full w-full cursor-pointer appearance-none border-0 bg-transparent opacity-0 z-1"
      />
      <div class={className ?? 'm-auto'}>{value}</div>
    </div>
  )
}

/**
 *
 * Handles line item quantity update.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemQuantity = ({ item, handleSetCartConfig }) => {
  const plusIcon = <Icon id="plus" className="w-4 h-4" />
  const minusIcon = <Icon id="minus" className="w-4 h-4" />
  const trashIcon = <Icon id="trash" className="w-4 h-4" />
  const [error, setError] = useState(null)

  /** Checks if the current Line Item new quantity is above the available inventory  */
  const handleLineItemQuantityChange = (item, newQuantity) => {
    if ((newQuantity > item.inventory_quantity) && newQuantity !== 0) {
      setError('Maximum quantity reached')
      setTimeout(() => setError(null), 5000)
      throw new Error('Unavailable product stock quantity')
    }
    setError(null)
  }

  const onLineItemQuantityChange = async (e) => {
    try {
      e.preventDefault()
      /** Get Action Key */
      const action = e.target.dataset.action
      /** Set MiniCart state to isLoading true */
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      /** Get new line item quantity based off action */
      const newQuantity = onChangeActionValue(action, item.quantity)

      /** Throw an error is the new quantity is over the available stock */
      handleLineItemQuantityChange(item, newQuantity)

      /** Update line item quantity */
      await window.Cart.updateLineItemQuantity(
        {
          id: item.variant_id,
          quantity: newQuantity
        },
        { silent: false }
      )

      /** Poll Shopify for new Cart item and set cart config state */
      await window.Cart.getCart()
      handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    } catch (error) {
      globalEmitter.emit(emitterProps.notice.global, {
        type: 'error',
        content: error.message
      })
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  return (
    <>
      <form onChange={onLineItemQuantityChange} class="flex flex-row justify-between items-center py-4 pb-0">
        <div class="flex flex-row flex-wrap border border-solid border-neutral-medium rounded-sm">
          <OnChangeTrigger p='px-1' type={actionKeys.MINUS} value={minusIcon} />
          <div class="paragraph-md py-1 w-14 flex-grow px-4 text-center border-x border-neutral-medium">{item.quantity}</div>
          <OnChangeTrigger p='px-1' type={actionKeys.PLUS} value={plusIcon} />
        </div>

        <OnChangeTrigger
          p="p-2 pr-0 pb-0"
          type={actionKeys.REMOVE}
          value={trashIcon}
          className={'btn btn-primary-text wavy-underline group-hover:show-wavy-underline pb-2'}
        />
      </form>
      <p class={clsx('paragraph-sm text-system-error opacity-0 transition-all duration-300 w-full pb-1 max-h-0 ', error && 'opacity-100 max-h-[18px]')}>{error || '\u00A0'}</p>
    </>
  )
}

/**
 *
 *
 * Mini Cart Snippet,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartSnippet({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }

  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const message =
    reachedGoal <= 0 ? 'You have free shipping' : `You're ${formatMoney(reachedGoal)} away from free shipping`
  const progressBarPercentage = Math.round((cartSubTotal / freeShippingGoal) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <div data-role="free_shipping_monitor" class="flex flex-col gap-4 p-4">
      <p class="subtitle-md text-center">{message}</p>
      <div class="relative h-2 w-full bg-neutral-medium rounded-lg">
        <div
          style={{ width: progressBarPercentageWidth + '%' }}
          class="absolute left-0 h-full bg-brand-green rounded-lg transition-all"
        ></div>
      </div>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const DiscountedPrice = ({ newPrice, oldPrice }) => {
    return (
      <>
        <p class="paragraph-md text-brand-sale">{formatMoney(newPrice)}</p>
        <s class="paragraph-md ">{formatMoney(oldPrice)}</s>
      </>
    )
  }

  if (item.discounts.length > 0 && item.line_price < item.original_line_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.compare_at_price * item.quantity} />
  }
  return <p class="paragraph-md">{formatMoney(item.original_line_price)}</p>
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts.map((discount) => {
        return <div class="caption uppercase text-neutral-black items-center flex gap-1"><Icon id={'tag'} className={'icon-md'} />{discount.title}</div>
      })}
    </>
  )
}

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath, productTitle }) => {
  return (
    <a href={productPath} class="aspect-4/5 w-32 flex-grow-0" aria-label={productTitle}>
      <PreactImage imageClass="contain" className={'h-full w-full object-contain'} key={id} imageUrl={[imageSrc]} alt={productTitle} />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  const properties = item.properties
  const hasProperties = properties && Object.keys(properties).length > 0

  return (
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-2">
        <div>
          <a href={item.url} class="paragraph-md wavy-underline pb-2">{item.product_title}</a>
          {item.options_with_values.map((option) => (
            <p class="paragraph-sm text-neutral-secondary">
              {option.name}: {option.value}
            </p>
          ))}
          {hasProperties && (
            <x-modal data-id={`mini-cart-properties-${item.id}`} class="dropdown-modal group">
              <button data-modal-role="toggle" aria-label="Toggle Product Details" type="button" data-modal-parent={`mini-cart-properties-${item.id}`} class="paragraph-md text-brand-black underline flex flex-row gap-1 items-center">Details <Icon id="chevron-down" className="transition-transform duration-300 icon-md group-data-visible:rotate-180" /></button>
              <div class="dropdown-content">
                <div class="overflow-hidden">
                  {properties && Object.keys(properties).map((property) => {
                    if (property.startsWith('_')) {
                      return null
                    }
                    return (
                      <p class="paragraph-sm first-of-type:mt-2 text-neutral-secondary">
                        <span class="text-neutral-black">{property}</span>: {properties[property]}
                      </p>
                    )
                  })}
                </div>
              </div>
            </x-modal>
          )}
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <ItemPrice item={item} />
      </div>
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading ? 'opacity-45' : ''

  return (
    <>
      <div class={`flex flex-row gap-2 p-4 transition-all ${loadingState}`}>
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} productTitle={item.product_title} />
        <div class="flex w-full flex-grow flex-col justify-between">
          <MiniCartItemDetails item={item} />
          <MiniCartItemQuantity item={item} handleSetCartConfig={handleSetCartConfig} />
          <MiniCartItemDiscount item={item} />
        </div>
      </div>
    </>
  )
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading }) => {
  const cartTotal = formatMoney(total)
  const totalDiscount = formatMoney(totalCartDiscount)
  const subtotal = formatMoney(total + totalCartDiscount)

  return (
    <div class="flex flex-col justify-between gap-1 pt-6 pb-1">
      {totalCartDiscount > 0 && (
        <>
          <div class="flex flex-row justify-between">
            <h5 class="paragraph-md text-neutral-black">Subtotal</h5>
            <h5 class={`paragraph-md text-neutral-black transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>{subtotal}</h5>
          </div>
          <div class="flex flex-row justify-between pb-0">
            <h5 class="paragraph-md text-neutral-black">Discounts</h5>
            <h5 class={`paragraph-md text-neutral-black transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>-{totalDiscount}</h5>
          </div>
        </>
      )}
      <div class="flex flex-row justify-between">
        <h5 class="h7">Total</h5>
        <h5 class={`h7 transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>{cartTotal}</h5>
      </div>
      <p className={'paragraph-sm text-neutral-secondary'}>Shipping, Gift Cards & Promo Codes calculated at checkout.</p>
    </div>
  )
}

/**
 *
 * Empty Cart state. Renders when the customers cart is empty
 *
 * @param {*} param0
 * @returns
 */
const EmptyCart = ({ isLoading, emptyDescription, emptyLink, emptyLinkText }) => {
  return (
    <>
      <div class={`p-4 grow min-h-14 py-24 flex flex-col justify-center items-center text-center transition-opacity ${isLoading ? 'opacity-0' : ''}`}>
        <p class={'paragraph-md'}>{emptyDescription}</p>
        {emptyLink &&
          <a href={emptyLink} className={'btn btn-md btn-primary my-2'} >{emptyLinkText}</a>
        }
      </div>
    </>
  )
}

/**
 *
 * Cart Checkout button
 *
 * @param {*} param0
 * @returns
 */
const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div class="pt-4 pb-2">
      <a class="btn btn-lg btn-accent-2 uppercase justify-center" href={`${globalState.routes.root}/checkout`}>
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div class="grow overflow-y-auto pb-20 divide-y divide-neutral-medium" data-role="cart_items">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
export function MiniCart() {
  const path = useURL()
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const globalCart = globalState.cart
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = globalCart?.freeShippingMinimum

  const { checkoutButtonTitle, emptyDescription, emptyLink, emptyLinkText } = globalCart

  useEffect(() => {
    const altPaymentButtons = document.querySelector('div.additional-checkout-buttons--vertical')
    if (!altPaymentButtons) return
    if (cart.items.length > 0) {
      altPaymentButtons.classList.remove('hidden')
    } else if (cart.items.length <= 0) {
      altPaymentButtons.classList.add('hidden')
    }
  }, [cartConfig])

  useEffect(() => {
    if (path.includes('timestamp=')) {
      handleSetCartConfig(CartConfigKeys.isLoading, true)
      window.Cart.getCart().then(() => {
        handleSetCartConfig(CartConfigKeys.cart, window.Cart.value)
        handleSetCartConfig(CartConfigKeys.isLoading, false)
      })
    }
  }, [path])

  return (
    <>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-shipping">
            <MiniCartSnippet cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            <div className={'flex flex-col bg-neutral-medium scallop p-4 max-h-fit sticky bottom-0'}>
              <DisplayCartTotal totalCartDiscount={cart.total_discount} total={cart.total_price} isLoading={isLoading} />
              <CheckoutButton checkoutButtonTitle={checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : (
        <MiniCartPortal targetId="minicart-items">
          <EmptyCart cartConfig={cartConfig} globalCart={globalCart} isLoading={isLoading} emptyDescription={emptyDescription} emptyLink={emptyLink} emptyLinkText={emptyLinkText} />
        </MiniCartPortal>
      )}
    </>
  )
}

const MiniCartPortal = ({ children, targetId }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}
